import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Phone, Car, Wrench, DollarSign, AlertCircle, ArrowRight, ChevronDown } from 'lucide-react';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';

const Warranty = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const basicFeatures = [
    'Engine Components',
    'Transmission Coverage',
    'Drive Axle',
    'Basic Roadside Assistance',
    'Towing Benefits'
  ];

  const premiumFeatures = [
    'All Basic Protection Features',
    'Electrical System',
    'Steering Components',
    'Braking System',
    'Air Conditioning',
    'Enhanced Roadside Package'
  ];

  const ultimateFeatures = [
    'All Premium Protection Features',
    'Fuel System Components',
    'Cooling System',
    'High-Tech Components',
    'Enhanced Labor Coverage',
    'Zero Deductible Option'
  ];

  const benefits = [
    'Protection against unexpected repair costs',
    'Roadside assistance wherever and whenever you need it',
    'Increased vehicle resale value',
    'Peace of mind while driving',
    'Customizable options to fit your needs'
  ];

  const europeanFeatures = [
    'Coverage for complex European vehicle electronics',
    'Protection for specialized components in BMW, Mercedes, Audi & Porsche',
    'Integration with our European vehicle service records',
    'Direct coordination with our European auto technicians'
  ];

  const faqs = [
    {
      question: 'When should I purchase a warranty plan?',
      answer: 'The best time to purchase a warranty is before your manufacturer\'s warranty expires or at the time of vehicle purchase. This ensures continuous coverage and often results in better rates.'
    },
    {
      question: 'Do your warranty plans cover European vehicle components?',
      answer: 'Yes! Our warranty plans are designed to work seamlessly with European vehicles. As Dallas\'s trusted European auto specialists, we ensure all warranty plans properly cover the sophisticated components found in BMW, Mercedes, Audi, and other European makes.'
    },
    {
      question: 'How does your warranty work with your European auto repair service?',
      answer: 'Our warranty plans are fully integrated with our European auto repair services. When your European vehicle needs service, simply bring it to our facility, and we\'ll handle both the repairs and warranty claims in one seamless process.'
    },
    {
      question: 'How do I file a claim if my vehicle breaks down?',
      answer: 'Simply bring your vehicle to Paradise-Performance or any ASE-certified repair facility, provide them with your warranty information, and we\'ll handle the rest. There\'s no need to pay upfront or file paperwork yourself.'
    },
    {
      question: 'Is my warranty transferable if I sell my vehicle?',
      answer: 'Yes! All our warranty plans are transferable to a new owner, which can significantly increase your vehicle\'s resale value and appeal to potential buyers.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Vehicle Warranty Plans | Extended Warranty Coverage | Paradise-Performance</title>
        <meta name="description" content="Protect your investment with comprehensive vehicle warranty plans. Save up to $3,500 on repairs with coverage for European and domestic vehicles." />
        <link rel="canonical" href="https://paradise-performance.com/warranty" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Vehicle Warranty Plans - Paradise-Performance" />
        <meta property="og:description" content="Comprehensive vehicle warranty coverage. Save thousands on unexpected repairs. European vehicle specialists." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/warranty" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Vehicle Warranty Plans",
            "description": "Comprehensive vehicle warranty coverage from Paradise-Performance",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Paradise-Performance",
              "telephone": "+1-817-382-2316",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "305 Co Rd 3470",
                "addressLocality": "Paradise",
                "addressRegion": "TX",
                "postalCode": "76073"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Warranty Plans"
        title="Vehicle Warranty Plans"
        subtitle="Comprehensive protection for your investment from Paradise-Performance"
      >
        <div className="flex flex-col items-center gap-8">
          <div className="inline-flex flex-col items-center rounded-2xl border border-red-500/30 bg-red-600/15 px-10 py-5 text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-red-300">SAVE UP TO</div>
            <div className="text-4xl font-bold text-white">$3,500</div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-red-300">ON REPAIRS</div>
          </div>
          <a
            href="tel:8173822316"
            className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
          >
            <Phone className="mr-2 h-5 w-5" />
            Get Your Quote Today
          </a>
        </div>
      </PageHero>

      {/* Main Content */}
      <div className="bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Intro Section */}
          <motion.section {...fadeInUp} className="py-20 md:py-24">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-10">
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-white md:text-4xl">Comprehensive Vehicle Warranty</h2>
              <p className="mb-6 text-lg leading-relaxed text-gray-300">
                Don't let unexpected repairs derail your budget. Our comprehensive warranty plans keep you covered when you need it most.
              </p>

              <div className="mb-6 rounded-2xl border border-red-500/20 bg-gradient-to-br from-red-600/15 to-white/[0.02] p-6">
                <h3 className="mb-4 text-xl font-bold text-white">Get Your Car Warranty At Paradise-Performance</h3>
                <p className="text-gray-300">
                  In conjunction with our European auto repair & Service Department, Paradise-Performance offers full warranty
                  packages for your used cars. Don't take the chance of a costly repair. Let us do the service and let the warranty
                  pick up the bill!
                </p>
              </div>

              <p className="mb-4 leading-relaxed text-gray-400">
                At Paradise-Performance, we are a faith-based, family-owned business dedicated to providing high-quality vehicles
                and services to Dallas drivers all the way to Wise County, TX (plus any of the surrounding areas). This commitment
                extends to our comprehensive warranty plans.
              </p>

              <p className="leading-relaxed text-gray-400">
                We believe that trust is the foundation of every successful transaction. As a faith-based business, we operate
                with the understanding that integrity matters in everything we do.
              </p>
            </div>
          </motion.section>

          {/* Why Choose Section */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <SectionHeading
              eyebrow="Peace Of Mind"
              title="Why Choose Our Warranty Plans"
              subtitle="Protect your investment with comprehensive coverage designed to give you peace of mind."
            />

            <div className="grid gap-6 md:grid-cols-3">
              <div className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                  <Shield className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-white">Comprehensive Coverage</h3>
                <p className="leading-relaxed text-gray-400">
                  From basic protection of critical components to bumper-to-bumper coverage, our plans are designed to fit your needs and budget.
                </p>
              </div>

              <div className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                  <Car className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-white">Roadside Assistance</h3>
                <p className="leading-relaxed text-gray-400">
                  Never worry about being stranded. Our roadside assistance helps wherever you are, whenever you need it.
                </p>
              </div>

              <div className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                  <DollarSign className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-white">Financial Protection</h3>
                <p className="leading-relaxed text-gray-400">
                  Safeguard yourself against unexpected, costly repairs that can put a strain on your finances.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Protection Plans */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <SectionHeading
              eyebrow="Coverage Levels"
              title="Vehicle Protection Plan Details"
              subtitle="Choose the coverage level that's right for you and your vehicle."
            />

            <div className="grid items-start gap-6 lg:grid-cols-3">
              {/* Basic Plan */}
              <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] transition-all duration-300 hover:border-white/20">
                <div className="border-b border-white/10 p-6">
                  <h3 className="mb-2 text-2xl font-bold text-white">Basic Protection</h3>
                  <p className="text-gray-400">Coverage for the most expensive and critical vehicle components.</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {basicFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:8173822316" className="mt-6 block rounded-xl border border-white/20 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-white hover:text-black">
                    Get Quote
                  </a>
                </div>
              </div>

              {/* Premium Plan */}
              <div className="relative overflow-hidden rounded-2xl border border-red-500/40 bg-white/[0.04] shadow-2xl shadow-red-900/20 transition-all duration-300 lg:scale-105">
                <div className="bg-gradient-to-br from-red-600 to-red-700 p-6">
                  <div className="mb-2 inline-block rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">MOST POPULAR</div>
                  <h3 className="mb-2 text-2xl font-bold text-white">Premium Protection</h3>
                  <p className="text-red-100">Expanded coverage for comprehensive vehicle protection.</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {premiumFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:8173822316" className="mt-6 block rounded-xl bg-red-600 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-red-700">
                    Get Quote
                  </a>
                </div>
              </div>

              {/* Ultimate Plan */}
              <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] transition-all duration-300 hover:border-white/20">
                <div className="border-b border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent p-6">
                  <h3 className="mb-2 text-2xl font-bold text-white">Ultimate Protection</h3>
                  <p className="text-gray-400">Our most comprehensive bumper-to-bumper coverage.</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {ultimateFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:8173822316" className="mt-6 block rounded-xl border border-white/20 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-white hover:text-black">
                    Get Quote
                  </a>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Used Vehicle Protection */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-10">
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-white md:text-4xl">Used Vehicle Protection Plans</h2>
              <p className="mb-6 text-lg leading-relaxed text-gray-300">
                New vehicles may be covered by the manufacturer's warranty, but once that has run out, you are responsible for
                any breakdowns that might occur. By adding a Used Vehicle Protection Plan to your vehicle, you will help offset
                the cost of any repairs you may need.
              </p>

              <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-6">
                <h3 className="mb-4 text-xl font-bold text-white">Benefits of Our Warranty Plans:</h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                      <span className="text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.section>

          {/* European Vehicle Coverage */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-red-600/20 via-white/[0.03] to-white/[0.03] p-8 md:p-12">
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-white md:text-4xl">European Vehicle Specialty Coverage</h2>
              <p className="mb-8 text-xl text-gray-300">
                Specialized warranty protection designed for luxury European vehicles serviced at our Dallas-area facility.
              </p>

              <div className="grid gap-5 md:grid-cols-2">
                {europeanFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
                    <Shield className="mr-3 mt-0.5 h-6 w-6 flex-shrink-0 text-red-500" />
                    <span className="text-gray-200">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a href="tel:8173822316" className="rounded-xl bg-red-600 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-red-700">
                  Get European Vehicle Coverage
                </a>
                <Link to="/services/european-car-repair" className="rounded-xl border border-white/30 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-white hover:text-black">
                  View Our European Services
                </Link>
              </div>
            </div>
          </motion.section>

          {/* FAQ Section */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-24">
            <SectionHeading
              eyebrow="Questions"
              title="Frequently Asked Questions"
              subtitle="Get answers to common questions about our warranty plans."
            />

            <div className="mx-auto max-w-4xl space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] transition-colors open:border-red-500/30 open:bg-white/[0.05]"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-6">
                    <span className="flex items-start gap-3 text-lg font-bold text-white">
                      <AlertCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-red-500" />
                      {faq.question}
                    </span>
                    <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-300 group-open:rotate-180" />
                  </summary>
                  <p className="px-6 pb-6 pl-[3.75rem] leading-relaxed text-gray-400">{faq.answer}</p>
                </details>
              ))}
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.section {...fadeInUp} className="pb-20 md:pb-28">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-red-600/20 via-white/[0.03] to-white/[0.03] p-10 text-center md:p-14">
              <h2 className="mb-4 text-3xl font-bold tracking-tight text-white md:text-4xl">Protect Your Investment Today</h2>
              <p className="mx-auto mb-6 max-w-3xl text-xl text-gray-300">
                Don't wait until it's too late. A Paradise-Performance warranty can save you thousands in unexpected repair costs.
              </p>
              <p className="mb-8 text-lg text-gray-400">
                Our warranty specialists will help you find the perfect plan for your vehicle and budget.
              </p>

              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <a
                  href="tel:8173822316"
                  className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-xl font-bold text-white transition-colors hover:bg-red-700"
                >
                  <Phone className="mr-3 h-5 w-5" />
                  Call (817) 382-2316
                </a>
                <Link
                  to="/service"
                  className="inline-flex items-center justify-center rounded-xl border border-white/30 px-8 py-4 text-xl font-bold text-white transition-colors hover:bg-white hover:text-black"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Link>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default Warranty;
